import React, {CSSProperties, useMemo} from 'react'
import styled from 'styled-components'
import {WINDOW_MODE_MOBILE_WIDTH, WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {getCurrencyValue, getWithdrawInfoDate, parseDate} from 'utils'
import {Paragraph} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {TransactionDetailContentRedesignedProps} from './TransactionDetailContentProps'
import {TransactionDetailContentHeader} from '../ContentHeader'
import {CopyInvoice} from '../CopyInvoice'

const StyledContainer = styled.div<CSSProperties>`
  display: flex;
  margin: ${convertUnit(25)};
  padding: ${convertUnit(25)} 0;
  border-radius: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  align-self: center;
  overflow: auto;

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    flex-direction: column;
    padding: ${convertUnit(25)};
  }
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    min-height: 100%;
    width: calc(100% - ${convertUnit(50)});
    margin: 0;
  }
`

const StyledContentSection = styled.div<CSSProperties>`
  display: flex;
  width: ${({width}) => width};
  flex-direction: column;
  margin: 0 ${convertUnit(25)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: 100%;
    margin: 0;
    margin-top: ${({marginTop}) => marginTop};
  }
`

const StyledInfoContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-right: ${convertUnit(25)};
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    margin-right: 0;
  }
`

const StyledInfoItem = styled.div`
  width: 50%;
  margin-bottom: ${convertUnit(16)};
`

const StyledWithdrawMessageContainer = styled.div`
  ${({theme}) => ({
    backgroundColor: theme.white_3,
  })}
  border-radius: ${convertUnit(8)};
  display: flex;
  justify-content: flex-start;
  width: 100%;
  height: fit-content;
  flex-direction: column;
  box-sizing: border-box;
  padding: ${convertUnit(8)} ${convertUnit(16)};
  margin-right: ${convertUnit(25)};
  border: 1px solid ${({theme}) => theme.gray_1};

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    margin-right: 0;
  }

  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    width: unset;
    padding: ${convertUnit(25)};
    margin-left: ${convertUnit(-25)};
    margin-right: ${convertUnit(-25)};
    background: ${({theme}) => theme.white_3};
    border-top: 1px solid ${({theme}) => theme.gray_1};
    border-bottom: 1px solid ${({theme}) => theme.gray_1};
    border-radius: 0;
  }
`

const StyledPricingContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${convertUnit(25)} 0;
  margin-bottom: ${convertUnit(20)};

  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    padding: ${convertUnit(25)};
    background: ${({theme}) => theme.white_3};
    margin-right: ${convertUnit(-25)};
    margin-left: ${convertUnit(-25)};
    width: calc(100%);
  }
  border-top: 1px solid ${({theme}) => theme.gray_1};
  border-bottom: 1px solid ${({theme}) => theme.gray_1};
`

const StyledPricing = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const StyledTextHeader = styled(Paragraph)`
  margin-bottom: ${convertUnit(20)};
`

const StyledText = styled(Paragraph)`
  white-space: pre-wrap;
  margin-bottom: ${convertUnit(8)};
`

const StyledSeparatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`

const StyledSeparator = styled.div`
  background-color: ${({theme}) => theme.gray_3};
  display: flex;
  flex: 1;
  height: ${convertUnit(1)};
  margin-top: ${convertUnit(10)};
`

const StyledSeparatorText = styled(Paragraph)`
  margin-left: ${convertUnit(5)};
  margin-bottom: ${convertUnit(10)};
`

export default function TransactionDetailContentWithdraw({
  stateData,
}: TransactionDetailContentRedesignedProps) {
  const {translate} = useTranslation()
  const userData = useSelector('user')

  const {
    invoice_id,
    status: stateDataStatus,
    payment_at,
    transaction_total,
    bank_account_name,
    bank_account_number,
    bank_name,
    bank_fee = 0,
    username,
  } = stateData
  const transaction_type = 'Withdraw'

  const date = useMemo(() => payment_at || new Date().toISOString(), [
    payment_at,
  ])

  const {checkDate, transferDate} = getWithdrawInfoDate(date)

  const withdrawInfoDateFormat = useMemo(
    () =>
      userData?.lang === 'en'
        ? 'dddd, MMMM D, YYYY, hh:mm A'
        : 'dddd, D MMMM YYYY, HH:mm',
    [userData?.lang],
  )

  const handleWithdrawInfo = useMemo(
    () => (
      <StyledWithdrawMessageContainer>
        <Paragraph
          fontSize="s"
          fontWeight="bold"
          style={{lineHeight: convertUnit(21)}}>
          {translate('giftShop:withdrawConfirmationExplanationFirst')}
        </Paragraph>
        <Paragraph
          fontSize="s"
          fontWeight="regular"
          style={{lineHeight: convertUnit(21)}}>
          {parseDate(checkDate, withdrawInfoDateFormat)}
        </Paragraph>
        <Paragraph
          fontSize="s"
          fontWeight="bold"
          style={{lineHeight: convertUnit(21)}}>
          {translate('giftShop:withdrawConfirmationExplanationSecond')}
        </Paragraph>
        <Paragraph
          fontSize="s"
          fontWeight="regular"
          style={{lineHeight: convertUnit(21)}}>
          {parseDate(transferDate, withdrawInfoDateFormat)}
        </Paragraph>
      </StyledWithdrawMessageContainer>
    ),
    [checkDate, transferDate, translate, withdrawInfoDateFormat],
  )

  const handleSeparator = useMemo(
    () => (
      <StyledSeparatorContainer>
        <StyledSeparator />
        <StyledSeparatorText fontWeight="bold" color="gray_3">
          {'+'}
        </StyledSeparatorText>
      </StyledSeparatorContainer>
    ),
    [],
  )

  const renderPriceBreakdown = useMemo(
    () => (
      <StyledPricingContainer>
        <StyledTextHeader fontWeight="bold" fontSize="l">
          {translate('giftShop:priceBreakdown')}
        </StyledTextHeader>

        <StyledPricing>
          <StyledText fontSize="m" fontWeight="medium">
            {translate('giftShop:nominal')}
          </StyledText>
          <StyledText fontSize="m">
            {getCurrencyValue(transaction_total + bank_fee)}
          </StyledText>
        </StyledPricing>
        {bank_fee ? (
          <StyledPricing>
            <Paragraph fontSize="m" fontWeight="medium">
              {translate('giftShop:pricefee_bank')}
            </Paragraph>
            <Paragraph fontSize="m">{getCurrencyValue(bank_fee)}</Paragraph>
          </StyledPricing>
        ) : (
          <></>
        )}
        {handleSeparator}
        <StyledPricing>
          <StyledText fontSize="m" fontWeight="medium">
            {translate('giftShop:totalWithdraw')}
          </StyledText>
          <StyledText fontSize="m" fontWeight="bold" color="primary_5">
            {getCurrencyValue(transaction_total)}
          </StyledText>
        </StyledPricing>
      </StyledPricingContainer>
    ),
    [bank_fee, handleSeparator, transaction_total, translate],
  )

  const renderContent = useMemo(
    () =>
      invoice_id && (
        <>
          <StyledContentSection width={'100%'}>
            <TransactionDetailContentHeader
              title={translate('giftShop:transactionType', {
                context: transaction_type.toUpperCase(),
              })}
              subTitle={bank_name}
              statuses={[
                {
                  status: stateDataStatus,
                  overrideStatusText:
                    stateDataStatus === 'PROCESSED'
                      ? translate('global:processed')
                      : undefined,
                  count: 1,
                },
              ]}
            />
            <StyledInfoContainer>
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:transactionTime')}
                </Paragraph>
                <Paragraph fontSize="m">
                  {(payment_at &&
                    parseDate(payment_at, 'MMMM D, YYYY, HH:mm')) ||
                    ''}
                </Paragraph>
              </StyledInfoItem>
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('global:username')}
                </Paragraph>
                <Paragraph fontSize="m">{username}</Paragraph>
              </StyledInfoItem>
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:accountName')}
                </Paragraph>
                <Paragraph fontSize="m">{bank_account_name}</Paragraph>
              </StyledInfoItem>
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:accountNumber')}
                </Paragraph>
                <Paragraph fontSize="m">{bank_account_number}</Paragraph>
              </StyledInfoItem>
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:invoiceID')}
                </Paragraph>
                <CopyInvoice invoice_id={invoice_id} context="single" />
              </StyledInfoItem>
            </StyledInfoContainer>
            {renderPriceBreakdown}
          </StyledContentSection>
        </>
      ),
    [
      invoice_id,
      translate,
      bank_name,
      stateDataStatus,
      payment_at,
      username,
      bank_account_name,
      bank_account_number,
      renderPriceBreakdown,
    ],
  )

  return (
    <StyledContainer>
      {renderContent}
      {handleWithdrawInfo}
    </StyledContainer>
  )
}
